/**
 * LedgerModal — create/edit a ledger.
 * @file src/web/features/admin/components/LedgerModal.tsx
 */

import { useEffect, useState } from 'react';
import { X, Loader2, Trash2, AlertTriangle } from 'lucide-react';
import { adminFetchJson } from '../lib/admin-api';

export interface LedgerLite { id: string; name: string; parent: string | null; openingBalance: number | null; gstin: string | null; source?: string }
interface Props {
  entityId: string | null;
  ledger: LedgerLite | null;
  /** Prefills the name when creating — the picker passes what was typed. */
  initialName?: string;
  onClose: () => void;
  onSaved: () => void;
}
interface GroupLite { id: string; name: string }

const inputCls = 'w-full rounded-xl border border-ink-200 bg-white px-4 py-2.5 text-sm text-ink-900 placeholder:text-ink-400 focus:border-inkblack focus:outline-none focus:ring-2 focus:ring-highlighter';
const NATURES = ['asset', 'liability', 'income', 'expense'] as const;

export function LedgerModal({ entityId, ledger, initialName, onClose, onSaved }: Props) {
  const editing = !!ledger;
  const [form, setForm] = useState({
    name: ledger?.name ?? initialName ?? '',
    parent: ledger?.parent ?? '',
    openingBalance: ledger?.openingBalance != null ? String(Math.abs(ledger.openingBalance)) : '',
    dc: (ledger?.openingBalance ?? 0) < 0 ? 'Dr' : 'Cr',
    gstin: ledger?.gstin ?? '',
  });
  const [saving, setSaving] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [typed, setTyped] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [groups, setGroups] = useState<GroupLite[]>([]);
  const [creatingGroup, setCreatingGroup] = useState(false);
  const [newGroup, setNewGroup] = useState({ name: '', nature: '' });
  const [addingGroup, setAddingGroup] = useState(false);
  const set = (k: string, v: string) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    adminFetchJson<{ items: GroupLite[] }>('/api/books/groups', { query: { entityId: entityId ?? undefined } })
      .then((d) => setGroups(d.items))
      .catch(() => setGroups([]));
  }, [entityId]);

  async function addGroup() {
    const nm = newGroup.name.trim();
    if (!nm) return;
    setAddingGroup(true); setError(null);
    try {
      const res = await adminFetchJson<{ id: string }>('/api/books/groups', { method: 'POST', query: { entityId: entityId ?? undefined }, body: JSON.stringify({ name: nm, nature: newGroup.nature || null }) });
      setGroups((gs) => [...gs, { id: res.id, name: nm }].sort((a, b) => a.name.localeCompare(b.name)));
      set('parent', nm);
      setCreatingGroup(false); setNewGroup({ name: '', nature: '' });
    } catch (e: any) { setError(e.message?.replace(/^\d+\s/, '') || 'Could not create group'); }
    finally { setAddingGroup(false); }
  }

  const save = async () => {
    setError(null);
    if (!form.name.trim()) { setError('Ledger name is required'); return; }
    setSaving(true);
    // Store back in Tally sign: Dr = negative, Cr = positive.
    const ob = form.openingBalance.trim() === '' ? null : (form.dc === 'Dr' ? -Math.abs(Number(form.openingBalance)) : Math.abs(Number(form.openingBalance)));
    const body = { entityId, name: form.name.trim(), parent: form.parent.trim(), openingBalance: ob, gstin: form.gstin.trim() };
    try {
      if (editing) await adminFetchJson(`/api/books/ledgers/${ledger!.id}`, { method: 'PATCH', body: JSON.stringify(body) });
      else await adminFetchJson('/api/books/ledgers', { method: 'POST', body: JSON.stringify(body) });
      onSaved();
    } catch (e: any) { setError(e.message?.replace(/^\d+\s/, '') || 'Could not save'); setSaving(false); }
  };

  /** Soft-deletes in the cloud and queues the deletion for Tally. The API
   *  refuses a ledger that still has postings, and that message is the useful
   *  one — surface it rather than a generic failure. */
  const remove = async () => {
    if (!ledger?.id) return;
    setSaving(true);
    try {
      await adminFetchJson(`/api/books/ledgers/${ledger.id}`, { method: 'DELETE' });
      onSaved();
    } catch (e: any) {
      setError(e.message?.replace(/^\d+\s/, '') || 'Could not delete');
      setSaving(false);
      setConfirming(false);
      setTyped('');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-ink-900/40" onClick={onClose} />
      <div className="relative z-10 w-full max-w-md rounded-2xl border border-ink-200 bg-white p-6 shadow-xl">
        <div className="mb-5 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-ink-900">{editing ? 'Edit ledger' : 'New ledger'}</h3>
          <button onClick={onClose} className="text-ink-400 hover:text-ink-700"><X className="h-5 w-5" /></button>
        </div>
        <div className="space-y-4">
          {error && <div className="rounded-xl border border-danger-200 bg-danger-50 px-3.5 py-2.5 text-sm text-danger-700">{error}</div>}
          {editing && ledger?.source !== 'manual' && <div className="rounded-xl border border-warn-200 bg-warn-50 px-3.5 py-2.5 text-xs text-warn-700">Editing a Tally-imported ledger — saving protects it from re-import overwrites.</div>}
          <div className="space-y-1.5"><label className="text-sm font-medium text-ink-700">Name</label><input value={form.name} onChange={(e) => set('name', e.target.value)} className={inputCls} /></div>
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-ink-700">Under group</label>
              <button type="button" onClick={() => setCreatingGroup((v) => !v)} className="text-xs font-semibold text-inkblack hover:text-inkblack">{creatingGroup ? 'Cancel' : '+ New group'}</button>
            </div>
            {creatingGroup ? (
              <div className="flex items-center gap-2">
                <input value={newGroup.name} onChange={(e) => setNewGroup((g) => ({ ...g, name: e.target.value }))} placeholder="New group name" className={inputCls} />
                <select value={newGroup.nature} onChange={(e) => setNewGroup((g) => ({ ...g, nature: e.target.value }))} className="shrink-0 rounded-xl border border-ink-200 bg-white px-3 py-2.5 text-sm capitalize focus:border-inkblack focus:outline-none focus:ring-2 focus:ring-highlighter">
                  <option value="">Nature…</option>
                  {NATURES.map((n) => <option key={n} value={n} className="capitalize">{n}</option>)}
                </select>
                <button type="button" onClick={addGroup} disabled={addingGroup || !newGroup.name.trim()} className="inline-flex shrink-0 items-center gap-1 rounded-xl bg-inkblack px-3.5 py-2.5 text-sm font-semibold text-white disabled:opacity-50">{addingGroup && <Loader2 className="h-4 w-4 animate-spin" />} Add</button>
              </div>
            ) : (
              <select value={form.parent} onChange={(e) => set('parent', e.target.value)} className={inputCls}>
                <option value="">— Ungrouped</option>
                {form.parent && !groups.some((g) => g.name === form.parent) && <option value={form.parent}>{form.parent} (current)</option>}
                {groups.map((g) => <option key={g.id} value={g.name}>{g.name}</option>)}
              </select>
            )}
            {groups.length === 0 && !creatingGroup && <p className="text-[11px] text-ink-400">No groups yet — use “+ New group”, or seed a standard chart in Masters → Groups.</p>}
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2 space-y-1.5"><label className="text-sm font-medium text-ink-700">Opening balance</label><input type="number" step="0.01" value={form.openingBalance} onChange={(e) => set('openingBalance', e.target.value)} className={inputCls} placeholder="0.00" /></div>
            <div className="space-y-1.5"><label className="text-sm font-medium text-ink-700">Dr/Cr</label>
              <select value={form.dc} onChange={(e) => set('dc', e.target.value)} className={inputCls}><option>Dr</option><option>Cr</option></select>
            </div>
          </div>
          <div className="space-y-1.5"><label className="text-sm font-medium text-ink-700">GSTIN</label><input value={form.gstin} onChange={(e) => set('gstin', e.target.value)} className={inputCls} placeholder="Optional" /></div>
        </div>
        {confirming && (
          <div className="mt-5 rounded-xl border border-danger-200 bg-danger-50 px-4 py-4">
            <p className="flex items-start gap-2 text-sm font-semibold text-danger-700">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              Delete “{ledger?.name}”?
            </p>
            {/* Accurate, not scary-generic: this is a soft delete that also asks
                Tally to remove it. Saying "permanent" here would be a lie, and
                not mentioning Tally would hide the consequential half. */}
            <ul className="mt-2 space-y-1 pl-6 text-xs text-danger-700">
              <li>It disappears from the app straight away.</li>
              <li>Its history is kept, so anything already filed still reads correctly.</li>
              <li>A request to delete it in Tally is queued — nothing changes in the
                  client's books until they confirm it in the desktop app.</li>
              <li>If it still has transactions, this will be refused.</li>
            </ul>
            <p className="mt-3 text-xs text-danger-700">Type <span className="font-semibold">yes</span> to confirm.</p>
            <div className="mt-2 flex items-center gap-2">
              <input
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                placeholder="yes"
                autoFocus
                className="w-28 rounded-lg border border-danger-300 bg-white px-3 py-1.5 text-sm focus:border-danger-500 focus:outline-none"
              />
              <button
                onClick={remove}
                disabled={typed.trim().toLowerCase() !== 'yes' || saving}
                className="inline-flex items-center gap-1.5 rounded-full bg-danger-600 px-4 py-1.5 text-sm font-semibold text-white disabled:opacity-40"
              >
                {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Delete ledger
              </button>
              <button onClick={() => { setConfirming(false); setTyped(''); }} className="rounded-full px-3 py-1.5 text-sm font-medium text-ink-600 hover:text-ink-800">Keep it</button>
            </div>
          </div>
        )}

        <div className="mt-6 flex items-center justify-end gap-2">
          {editing && !confirming && (
            <button
              onClick={() => setConfirming(true)}
              className="mr-auto inline-flex items-center gap-1.5 rounded-full border border-danger-200 bg-white px-4 py-2.5 text-sm font-semibold text-danger-600 hover:bg-danger-50"
            >
              <Trash2 className="h-4 w-4" /> Delete
            </button>
          )}
          <button onClick={onClose} className="rounded-full border border-ink-300 bg-white px-5 py-2.5 text-sm font-semibold text-ink-700 hover:bg-ink-50">Cancel</button>
          <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 rounded-full bg-inkblack px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-60">{saving && <Loader2 className="h-4 w-4 animate-spin" />} {editing ? 'Save changes' : 'Create ledger'}</button>
        </div>
      </div>
    </div>
  );
}
